import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_CHIPS = [
  { id: "what-is-saleyes", label: "What is SaleYes?" },
  { id: "ai-for-msme", label: "How can AI help my business?" },
  { id: "book-demo", label: "Book a demo" },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm the YesAI assistant. Ask me anything about running your business smarter with AI.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const patchLast = (content) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content };
      return copy;
    });

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + payload.delta };
                return copy;
              });
            } else if (payload.error) {
              patchLast(payload.error);
            }
          } catch (e) {
            /* partial json, wait for more */
          }
        }
      }
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't fetch an answer right now. Please try again." };
        }
        return copy;
      });
    } catch (e) {
      patchLast("Something went wrong. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="ai-chat-launcher"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] w-14 h-14 rounded-full bg-brand-red hover:bg-brand-redDark text-white shadow-xl shadow-brand-red/30 flex items-center justify-center transition-colors duration-200"
        aria-label="Open YesAI assistant"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-blue rounded-full border-2 border-white" data-testid="ai-chat-badge" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-[5.5rem] right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm h-[560px] max-h-[72vh] bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-slate-900/20 flex flex-col overflow-hidden"
          >
            <div className="bg-brand-red text-white px-5 py-4 flex items-center gap-3">
              <span className="h-10 rounded-xl bg-white flex items-center px-2.5 py-1.5 shadow-sm">
                <img src="/yesai-logo.png" alt="YesAI" className="h-full w-auto" />
              </span>
              <div className="flex-1">
                <p className="font-display font-bold text-sm">YesAI Assistant</p>
                <p className="text-[11px] text-white/70">Your Everyday Smart AI</p>
              </div>
              <button
                data-testid="ai-chat-close-btn"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors duration-200"
                aria-label="Close assistant"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-brand-ink text-white rounded-br-md"
                        : "bg-slate-100 text-slate-700 rounded-bl-md"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 py-1">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.15s]" />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.3s]" />
                      </span>
                    ) : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length === 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="ai-chat-quick-chips">
                {QUICK_CHIPS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`chat-chip-${c.id}`}
                    onClick={() => send(c.label)}
                    className="text-xs font-semibold text-brand-red bg-brand-redSoft hover:bg-red-100 border border-brand-red/20 px-3 py-1.5 rounded-full transition-colors duration-200"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            <form
              className="p-3 border-t border-slate-100 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your business..."
                className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/10 transition-colors duration-200"
              />
              <button
                data-testid="ai-chat-send-btn"
                type="submit"
                disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-full bg-brand-red hover:bg-brand-redDark disabled:opacity-40 text-white flex items-center justify-center transition-colors duration-200"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
